'use client';

import type { ReactNode } from 'react';

import type { LucideIcon } from 'lucide-react';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@kit/ui/button';
import { FormLabel } from '@kit/ui/form';
import { Input, type InputProps } from '@kit/ui/input';
import { useResponsivePopup } from '@kit/ui/responsive-popup';
import { cn } from '@kit/ui/utils';

interface IconInputProps extends InputProps {
  icon: LucideIcon;
}

export function IconInput({ icon: Icon, className, ...props }: IconInputProps) {
  return (
    <div className="relative">
      <Icon className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
      <Input {...props} className={`pl-10 ${className ?? ''}`} />
    </div>
  );
}

interface RequiredFormLabelProps {
  children: ReactNode;
}

export function RequiredFormLabel({ children }: RequiredFormLabelProps) {
  return (
    <FormLabel>
      {children}
      <span className="text-destructive ml-0.5">*</span>
    </FormLabel>
  );
}

/**
 * Wizard button row. In the phone sheet it sticks to the bottom so the
 * buttons stay reachable while the fields scroll.
 */
export function WizardFooterBar({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  const isSheet = useResponsivePopup()?.mode === 'sheet';

  return (
    <div
      className={cn(
        'flex pt-4',
        isSheet &&
          'bg-background sticky bottom-0 -mx-5 mt-4 border-t px-5 py-3',
        className,
      )}
    >
      {children}
    </div>
  );
}

/**
 * Secondary wizard actions ("Wstecz", "Pomiń na razie"): grey text with a
 * dotted underline, same as the severity legend link.
 */
export const WIZARD_SUBTLE_BUTTON =
  'h-7 px-1 text-zinc-500 underline decoration-zinc-300 decoration-dotted underline-offset-4 hover:bg-transparent hover:text-zinc-800 hover:decoration-zinc-500 dark:text-zinc-400 dark:decoration-zinc-600 dark:hover:bg-transparent dark:hover:text-zinc-200';

/** "Wstecz" with a left arrow, shared by every wizard step. */
export function WizardBackButton({ onClick }: { onClick: () => void }) {
  const { t } = useTranslation('account');

  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      onClick={onClick}
      className={WIZARD_SUBTLE_BUTTON}
    >
      <ArrowLeft className="mr-1.5 h-3.5 w-3.5" />
      {t('profileWizard.back')}
    </Button>
  );
}

interface WizardFormFooterProps {
  onBack: () => void;
  isSubmitting: boolean;
}

export function WizardFormFooter({
  onBack,
  isSubmitting,
}: WizardFormFooterProps) {
  const { t } = useTranslation('account');

  return (
    <WizardFooterBar className="items-end justify-between">
      <WizardBackButton onClick={onBack} />

      <Button type="submit" disabled={isSubmitting}>
        {isSubmitting ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            {t('profileWizard.saving')}
          </>
        ) : (
          t('profileWizard.submit')
        )}
      </Button>
    </WizardFooterBar>
  );
}
